import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarCheck, CalendarX, FileText, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Notification = {
  id: number;
  kind: string;
  title: string;
  detail: string | null;
  url: string | null;
  shop_id: number | null;
  read_at: string | null;
  created_at: string;
};

type Payload = { items: Notification[]; unread: number };

// Long-poll fallback cadence if SSE isn't available (proxy strips it,
// etc). Kept much shorter than the old 45s because the primary path
// is now push.
const FALLBACK_POLL_MS = 120_000;

export function NotificationBell() {
  const [payload, setPayload] = useState<Payload>({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<"unread" | "all">("unread");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get<Payload>(`/api/notifications?scope=${scope}`);
      setPayload(r);
    } catch { /* ignore transient failures */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Live push via SSE. Reconnect on error / server-side lifetime end.
  // A parallel setInterval acts as a fallback in case SSE gets blocked.
  useEffect(() => {
    let es: EventSource | null = null;
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      try {
        es = new EventSource("/api/sse/notifications", { withCredentials: true });
        es.addEventListener("wake", () => load());
        es.addEventListener("bye", () => { try { es?.close(); } catch {} if (!cancelled) setTimeout(connect, 500); });
        es.addEventListener("error", () => { try { es?.close(); } catch {} if (!cancelled) setTimeout(connect, 3_000); });
      } catch { setTimeout(connect, 3_000); }
    };
    connect();
    const fallback = setInterval(load, FALLBACK_POLL_MS);
    return () => { cancelled = true; try { es?.close(); } catch {} clearInterval(fallback); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const markRead = async (id: number) => {
    try {
      await api.patch(`/api/notifications/${id}/read`, {});
      // Optimistic — flip locally, refresh unread count from server.
      setPayload((p) => ({
        items: p.items.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n),
        unread: Math.max(0, p.unread - 1),
      }));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/api/notifications/mark-all-read");
      setPayload((p) => ({ items: p.items.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })), unread: 0 }));
    } catch { /* ignore */ }
  };

  const openOne = async (n: Notification) => {
    if (!n.read_at) await markRead(n.id);
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="size-4" />
          {payload.unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-[16px] items-center justify-center rounded-full bg-cta px-1 text-[10px] font-bold text-cta-foreground">
              {payload.unread > 99 ? "99+" : payload.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="flex gap-1 rounded-md bg-muted p-0.5 text-xs">
            <button
              onClick={() => setScope("unread")}
              className={cn("rounded px-2 py-0.5", scope === "unread" && "bg-background shadow-sm")}
            >Unread {payload.unread > 0 && `(${payload.unread})`}</button>
            <button
              onClick={() => setScope("all")}
              className={cn("rounded px-2 py-0.5", scope === "all" && "bg-background shadow-sm")}
            >All</button>
          </div>
          {payload.unread > 0 && (
            <button onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-[440px] overflow-y-auto scroll-slim">
          {payload.items.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {scope === "unread" ? "You're all caught up." : "No notifications yet."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {payload.items.map((n) => {
                const Icon = iconFor(n.kind);
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => openOne(n)}
                      className={cn(
                        "flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/50",
                        !n.read_at && "bg-primary/5"
                      )}
                    >
                      <div className={cn(
                        "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full",
                        n.read_at ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
                      )}>
                        <Icon className="size-3" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-xs", n.read_at ? "text-muted-foreground" : "font-medium")}>{n.title}</p>
                        {n.detail && <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{n.detail}</p>}
                        <p className="mt-1 text-[10px] text-muted-foreground">{formatRelative(n.created_at)}</p>
                      </div>
                      {!n.read_at && (
                        <span
                          role="button"
                          aria-label="Mark read"
                          onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                          className="mt-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-muted"
                        >
                          <X className="size-3" />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function iconFor(kind: string) {
  switch (kind) {
    case "booking-confirmed": return CalendarCheck;
    case "booking-noshow": return CalendarX;
    case "portal-submission": return FileText;
    default: return Sparkles;
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
